import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@material-ui/core';
import ReferenceTypeLinks from './ReferenceTypeLinks';
import { Reference } from './Reference.interface';

const StyledBox = styled(Box)`
  padding-top: 16px;
  padding-left: 16px;
  .type {
    margin-top: 2px;
    margin-bottom: 2px;
  }
  .description {
    white-space: break-spaces;
  }
`;

export interface ReferenceTypeListProps {
  show: boolean;
  className?: string;
  referenceType: Reference | undefined;
  onClickType?: (type: Reference) => void;
}
const ReferenceTypeList: React.FC<ReferenceTypeListProps> = (
  props: ReferenceTypeListProps
) => {
  const { show = false, className, referenceType, onClickType } = props;
  const [didOpen, setDidOpen] = useState<boolean>(show);

  // only render items if opened to avoid lag
  const items: JSX.Element[] = useMemo(() => {
    if (!didOpen || !referenceType) {
      return [];
    }
    return Object.entries(referenceType.props).map(([key, property]) => {
      const { id, name, type, description, isArray } = property;
      const shouldWrap = type && isArray;
      return (
        <ListItem key={key}>
          <ListItemText
            primaryTypographyProps={{ component: 'div' }}
            secondaryTypographyProps={{ component: 'div' }}
            primary={
              <>
                <Typography
                  component='div'
                  variant='subtitle1'
                  style={{ lineHeight: 'unset' }}
                >
                  <code>{id}</code>
                </Typography>
                {(type || isArray) && (
                  <Typography
                    variant='body2'
                    component='div'
                    className='type'
                    color='textPrimary'
                  >
                    <code>
                      {isArray && 'array'}
                      {shouldWrap && ' ('}
                      {typeof type === 'string' ? (
                        type
                      ) : (
                        <ReferenceTypeLinks refs={type} onClick={onClickType} />
                      )}
                      {shouldWrap && ')'}
                    </code>
                  </Typography>
                )}
              </>
            }
            secondary={
              <>
                {name && (
                  <Typography variant='body2' color='textSecondary'>
                    {name}
                  </Typography>
                )}
                {description && (
                  <Typography variant='body2' color='textSecondary'>
                    {description}
                  </Typography>
                )}
              </>
            }
          />
        </ListItem>
      );
    });
  }, [didOpen, referenceType, onClickType]);

  useEffect(() => {
    show && setDidOpen(true);
  }, [show]);

  return (
    <StyledBox className={className}>
      <Box mr={2}>
        <Typography variant='overline'>Available Properties</Typography>
      </Box>
      <List>{items}</List>
    </StyledBox>
  );
};

export default ReferenceTypeList;
